import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tag,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronRight, FiArrowLeft, FiSearch } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { apiGetViewAsOptions, apiGetViewAsUsersByBranch, apiGetViewAsUsersBySearch } from 'api';
import type { ViewAsState } from 'api';

export type { ViewAsState };

interface Branch { id: number; name: string }
interface OrgUser {
  email:        string;
  name:         string;
  role:         number;
  role_display: string;
  branch_name?: string;
  branch_id?:   number | null;
}

// Role badge colours — intentionally subtle
const ROLE_COLOURS: Record<string, string> = {
  'Manager':         'purple',
  'Broker':          'orange',
  'Broker Partner':  'teal',
  'Underwriter':     'blue',
  'CSR':             'gray',
  'TSR':             'gray',
  'Account Manager': 'cyan',
};

interface Props {
  viewAs:        ViewAsState;
  onChange:      (s: ViewAsState) => void;
  onClose:       () => void;
  anchorRef:     React.RefObject<HTMLElement | null>;
  isAdmin:       boolean;
  isManager:     boolean;
  initialBranch: Branch | null;
}

export const BrokerSearchPanel: React.FC<Props> = ({
  viewAs,
  onChange,
  onClose,
  anchorRef,
  isAdmin,
  isManager,
  initialBranch,
}) => {
  // Panel opens pre-navigated to initialBranch when a user or branch is already selected.
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(initialBranch);

  // Re-sync when the branch ID changes (panel reopened with different viewAs).
  // Intentionally dep on id only — parent creates a new object on each render
  // so using the full object would reset selectedBranch mid-navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSelectedBranch(initialBranch); }, [initialBranch?.id]);
  const [query, setQuery]                   = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const bg        = useColorModeValue('white',    'navy.800');
  const border    = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBg   = useColorModeValue('gray.50',  'navy.700');
  const activeBg  = useColorModeValue('orange.50','navy.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subText   = useColorModeValue('gray.400', 'gray.500');
  const headBg    = useColorModeValue('gray.50',  'navy.900');

  // ── Branches (always fetched, lightweight) ──────────────────────────────────
  const { data: optionsData } = useQuery<{ branches: Branch[] }>({
    queryKey: ['view-as-options'],
    queryFn:  () => apiGetViewAsOptions() as Promise<{ branches: Branch[] }>,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: isAdmin || isManager,
  });

  // ── Users in selected branch (on-demand) ────────────────────────────────────
  const { data: usersData, isLoading: loadingUsers } = useQuery<{
    branch: Branch | null;
    users:  OrgUser[];
  }>({
    queryKey: ['view-as-users', selectedBranch?.id],
    queryFn:  () => apiGetViewAsUsersByBranch(selectedBranch!.id) as Promise<{ branch: Branch; users: OrgUser[] }>,
    enabled:  !!selectedBranch,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // ── Global cross-branch search (no branch selected, query ≥ 3 chars) ────────
  const globalSearchActive = !selectedBranch && query.length >= 3;
  const { data: searchData, isLoading: loadingSearch } = useQuery<{
    branch: null;
    users:  OrgUser[];
  }>({
    queryKey: ['view-as-search', query],
    queryFn:  () => apiGetViewAsUsersBySearch(query) as Promise<{ branch: null; users: OrgUser[] }>,
    enabled:  globalSearchActive,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const branches = optionsData?.branches ?? [];
  const users    = usersData?.users      ?? [];

  // Branches still filtered locally for the branch-list view
  const filteredBranches = branches.filter((b) =>
    b.name.toLowerCase().includes(query.toLowerCase()),
  );

  // ── Close on outside click ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!panelRef.current?.contains(t) && !anchorRef.current?.contains(t)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  // Reset query when switching views
  useEffect(() => { setQuery(''); }, [selectedBranch]);

  // ── Position below anchor ────────────────────────────────────────────────────
  const rect = anchorRef.current?.getBoundingClientRect();
  const top  = (rect?.bottom ?? 0) + 6;
  const left = rect?.left    ?? 0;

  // Users within selected branch — filtered locally
  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(query.toLowerCase()) ||
    u.role_display.toLowerCase().includes(query.toLowerCase()),
  );

  const isBranchActive = viewAs.type === 'branch' && viewAs.branchId === selectedBranch?.id;

  return (
    <Box
      ref={panelRef}
      position="fixed"
      top={`${top}px`}
      left={`${left}px`}
      zIndex={1200}
      bg={bg}
      border="1px solid"
      borderColor={border}
      borderRadius="12px"
      boxShadow="0 8px 30px rgba(0,0,0,0.12)"
      w="300px"
      overflow="hidden"
    >

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      {selectedBranch ? (
        <Flex
          align="center"
          gap="8px"
          px="12px"
          py="10px"
          bg={headBg}
          borderBottom="1px solid"
          borderColor={border}
          cursor="pointer"
          _hover={{ bg: hoverBg }}
          onClick={() => setSelectedBranch(null)}
        >
          <Icon as={FiArrowLeft} w="14px" h="14px" color={subText} />
          <Text fontSize="xs" fontWeight="700" color={textColor} flex="1" noOfLines={1}>
            {selectedBranch.name}
          </Text>
          {/* Branch View button — triggers branch aggregate */}
          {(isAdmin || isManager) && (
            <Box
              px="8px"
              py="3px"
              borderRadius="20px"
              fontSize="11px"
              fontWeight="700"
              cursor="pointer"
              bg={isBranchActive ? 'orange.400' : 'orange.50'}
              color={isBranchActive ? 'white' : 'orange.600'}
              border="1px solid"
              borderColor="orange.200"
              onClick={(e) => {
                e.stopPropagation();
                onChange({
                  type:       'branch',
                  branchId:   selectedBranch.id,
                  branchName: selectedBranch.name,
                });
                onClose();
              }}
            >
              {isBranchActive ? '✓ Branch View' : 'View Branch'}
            </Box>
          )}
        </Flex>
      ) : (
        <Box
          px="12px"
          py="10px"
          bg={headBg}
          borderBottom="1px solid"
          borderColor={border}
        >
          <Text fontSize="xs" fontWeight="700" color={subText} textTransform="uppercase" letterSpacing="0.06em">
            Select a branch
          </Text>
        </Box>
      )}

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <Box px="10px" py="8px" borderBottom="1px solid" borderColor={border}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color={subText} w="14px" h="14px" />
          </InputLeftElement>
          <Input
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            border="none"
            bg={useColorModeValue('gray.50', 'navy.900')}
            borderRadius="8px"
            fontSize="sm"
            _focus={{ boxShadow: 'none' }}
            autoFocus
          />
        </InputGroup>
      </Box>

      {/* ── List ───────────────────────────────────────────────────────────── */}
      <Box maxH="280px" overflowY="auto">

        {/* Branch list — always shown when no branch selected (filtered locally) */}
        {!selectedBranch && (
          <>
            {globalSearchActive && filteredBranches.length > 0 && (
              <Box px="14px" pt="8px" pb="2px">
                <Text fontSize="10px" fontWeight="700" color={subText} textTransform="uppercase" letterSpacing="0.06em">
                  Branches
                </Text>
              </Box>
            )}
            {filteredBranches.length === 0 && !globalSearchActive ? (
              <Box px={4} py={3}>
                <Text fontSize="sm" color={subText}>No branches found</Text>
              </Box>
            ) : filteredBranches.map((branch) => (
              <Flex
                key={branch.id}
                px="14px"
                py="10px"
                align="center"
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => setSelectedBranch(branch)}
              >
                <Text fontSize="sm" color={textColor} flex="1" fontWeight="500">{branch.name}</Text>
                <Icon as={FiChevronRight} w="14px" h="14px" color={subText} />
              </Flex>
            ))}

            {/* Members section — only when 4+ chars */}
            {globalSearchActive && (
              <>
                <Box px="14px" pt="8px" pb="2px" borderTop={filteredBranches.length > 0 ? '1px solid' : 'none'} borderColor={border}>
                  <Text fontSize="10px" fontWeight="700" color={subText} textTransform="uppercase" letterSpacing="0.06em">
                    Members
                  </Text>
                </Box>
                {loadingSearch ? (
                  <Flex justify="center" py={4}>
                    <Spinner size="sm" color="orange.400" />
                  </Flex>
                ) : (searchData?.users ?? []).length === 0 ? (
                  <Box px={4} py={2}>
                    <Text fontSize="sm" color={subText}>No members found</Text>
                  </Box>
                ) : (searchData!.users).map((u) => {
                  const isActive = viewAs.type === 'broker' && viewAs.email === u.email;
                  const tagColor = ROLE_COLOURS[u.role_display] ?? 'gray';
                  return (
                    <Flex
                      key={u.email}
                      px="14px"
                      py="9px"
                      align="center"
                      gap="8px"
                      cursor="pointer"
                      bg={isActive ? activeBg : undefined}
                      _hover={{ bg: hoverBg }}
                      onClick={() => {
                        onChange({ type: 'broker', email: u.email, name: u.name, branchId: u.branch_id ?? undefined, branchName: u.branch_name ?? undefined });
                        onClose();
                      }}
                    >
                      <Box flex="1" minW="0">
                        <Text fontSize="sm" fontWeight={isActive ? '700' : '500'} color={textColor} noOfLines={1}>
                          {u.name}
                        </Text>
                        {u.branch_name && (
                          <Text fontSize="10px" color={subText} noOfLines={1}>{u.branch_name}</Text>
                        )}
                      </Box>
                      <Tag size="sm" colorScheme={tagColor} flexShrink={0} fontSize="10px">
                        {u.role_display}
                      </Tag>
                    </Flex>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* Users list */}
        {selectedBranch && (
          loadingUsers ? (
            <Flex justify="center" py={6}>
              <Spinner size="sm" color="orange.400" />
            </Flex>
          ) : filteredUsers.length === 0 ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color={subText}>No users found</Text>
            </Box>
          ) : filteredUsers.map((u) => {
            const isActive = viewAs.type === 'broker' && viewAs.email === u.email;
            const tagColor = ROLE_COLOURS[u.role_display] ?? 'gray';
            return (
              <Flex
                key={u.email}
                px="14px"
                py="9px"
                align="center"
                gap="8px"
                cursor="pointer"
                bg={isActive ? activeBg : undefined}
                _hover={{ bg: hoverBg }}
                onClick={() => {
                  onChange({ type: 'broker', email: u.email, name: u.name, branchId: selectedBranch?.id, branchName: selectedBranch?.name });
                  onClose();
                }}
              >
                <Box flex="1" minW="0">
                  <Text
                    fontSize="sm"
                    fontWeight={isActive ? '700' : '500'}
                    color={textColor}
                    noOfLines={1}
                  >
                    {u.name}
                  </Text>
                </Box>
                <Tag size="sm" colorScheme={tagColor} flexShrink={0} fontSize="10px">
                  {u.role_display}
                </Tag>
              </Flex>
            );
          })
        )}
      </Box>
    </Box>
  );
};
